import styled from '@emotion/styled';
import React, { forwardRef } from 'react';
import { segment } from '@actions/analytics';
import {
  PLANNER_VIDEO_CARD_CLICKED,
  PLANNER_PAGE,
  PLANNER_VIDEO_CARD_VIEWED
} from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import P2 from '@base/P2/P2';
import H5 from '@base/H5/H5';
import Image from '@base/Image';
import Button from '@base/Button/Button';
import { Spacings, Fonts } from '@styles/index';

import MUIDialog from '@material-ui/core/Dialog';
import Slide from '@material-ui/core/Slide';
import Youtube from '@cont/FreeLesson/externalyoutube';
import pushRoute from '@utils/pushRoute';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import reduxFreeFetcher from '@api/fetchers';
import {
  browseTopSpacing,
  dayTitleLeftSpacing,
  plannerItemTypeHeightMap
} from '../../planner-constants';

const OuterWrapper = styled.div`
  padding-top: ${browseTopSpacing}px;
  height: ${({ $height }) => $height}px;
`;

const Wrapper = styled.div`
  margin-left: ${dayTitleLeftSpacing};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  background-image: ${({ $imageURL }) => $imageURL && `url(${$imageURL})`};
  background-repeat: no-repeat;
  background-position: bottom right;
  background-size: cover;
  border-radius: ${Spacings.SPACING_2B};
  padding: ${({ $paddingTop }) => `${$paddingTop} ${Spacings.SPACING_8B}`};
  height: 100%;
`;

const StyP2 = styled(P2)`
  font-size: 10px;
  font-weight: ${Fonts.BOLD};
  margin-right: ${Spacings.SPACING_2};
  line-height: ${Spacings.SPACING_3B};
  text-transform: uppercase;
`;

const StyleP2 = styled(P2)`
  margin-top: ${Spacings.SPACING_1B};
  margin-bottom: ${Spacings.SPACING_3B};
  line-height: ${Spacings.SPACING_4B};
  max-width: 258px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const StyH5 = styled(H5)`
  max-width: 263px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_1B};
`;

const StyButton = styled(Button)`
  font-size: ${Fonts.ULTRA_SMALL};
  border-radius: ${Spacings.SPACING_1B};
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  width: auto;
  border: 0;
  background-color: var(--color-i-white);
  color: var(--color-i-black);
  &:hover {
    box-shadow: none;
    transform: translate(0px, 0px);
    border: 0;
    background-color: var(--color-i-white);
  }
  &:active {
    border: 0;
    box-shadow: none;
    background-color: var(--color-i-white);
  }
`;

const opts = {
  height: '100%',
  width: '100%',
  playerVars: {
    autoplay: 1,
    showinfo: 0,
    rel: 0
  }
};

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(MUIDialog)`
  &.MuiDialog-root .MuiDialog-container .MuiPaper-root {
    ${({ $center }) => !$center && 'position: absolute;'}
    ${({ $center }) => !$center && 'bottom: 0px;'}
    background: var(--color-text-primary);
    height: ${({ $height }) => $height};
    width: ${({ $width }) => $width};
    max-width: ${({ $width }) => $width};
    @media only screen and (max-width: 540px) {
      margin: 16px auto;
    }
  }
`;

const calcVideoId = (url) => {
  if (!url) return null;

  const videoid = url.match(
    /(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/
  );
  if (videoid != null) {
    return videoid[1];
  }
  return null;
};

const FeatureCard = ({ metaItem }) => {
  const { plannerItemType, itemInfo } = metaItem;
  const height = plannerItemTypeHeightMap[plannerItemType];

  const { item_info, object_uid, description, title, header_text } =
    itemInfo || {};

  const {
    web_deeplink,
    web_video_url,
    web_image_url,
    cta_text,
    card_css_property
  } = item_info || {};

  const { background_color, text_color } = card_css_property || {};
  const videoId = calcVideoId(web_video_url);
  const goalUID = useSelectedGoalUID();
  const goalSegmentData = useGoalEventProps({ goalUID });

  React.useEffect(() => {
    segment.track(PLANNER_VIDEO_CARD_VIEWED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      video_title: title,
      video_uid: object_uid
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [openVideo, setOpenVideo] = React.useState(false);
  const closeVideo = () => {
    setOpenVideo(false);
  };

  const handleClick = () => {
    segment.track(PLANNER_VIDEO_CARD_CLICKED, {
      ...goalSegmentData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      video_title: title,
      video_uid: object_uid
    });

    reduxFreeFetcher('v2/planner/cards/', {
      requestType: 'post',
      body: { card_uid: object_uid }
    }).then(() => {
      if (web_video_url) {
        setOpenVideo(true);
      } else if (web_deeplink) {
        pushRoute(web_deeplink, web_deeplink);
      }
    });
  };

  const showVideoDialog = videoId && openVideo;
  return (
    <>
      <OuterWrapper $height={height}>
        <Wrapper
          $backgroundColor={background_color}
          $imageURL={web_image_url}
          $paddingTop={header_text ? Spacings.SPACING_6B : Spacings.SPACING_7B}
        >
          {!!header_text && (
            <HeaderWrapper>
              <StyP2 color={text_color}>{header_text}</StyP2>
              <Image
                src="planner/feature_star.svg"
                width="11px"
                height={Spacings.SPACING_3B}
                alt="feature"
              />
            </HeaderWrapper>
          )}
          <StyH5 color={text_color}>{title}</StyH5>
          <StyleP2 color={text_color}>{description}</StyleP2>
          <StyButton
            variant="hollow"
            label={cta_text}
            color="black"
            maxHeight="xs"
            onClick={handleClick}
          />
        </Wrapper>
      </OuterWrapper>
      <StyledDialog
        open={showVideoDialog}
        TransitionComponent={Transition}
        onClose={closeVideo}
        $center={false}
        $height="80%"
        $width="88%"
      >
        <Youtube
          opts={opts}
          videoId={videoId}
          onEnd={closeVideo}
          useWrapper={false}
        />
      </StyledDialog>
    </>
  );
};

export default FeatureCard;
